import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Apollo, MutationResult } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import {
  CashRegister,
  CashRegisterIn,
  DeleteCashRegisterDocument,
  DeleteCashRegisterMutation,
  DeleteCashRegisterMutationVariables,
  GenericNumber,
  GetCashRegisterByBusinessUnitIdDocument,
  GetCashRegisterByIdDocument,
  GetCashRegisterByIdToPointEmissionDocument,
  GetCashRegisterCatalogsDocument,
  GetCashRegisterCatalogsQuery,
  GetCashRegisterCatalogsQueryVariables,
  GetCashRegisterToPointEmissionDocument,
  GetNextCashRegisterNumberDocument,
  IdDto,
  SaveCashRegisterDocument,
  SaveCashRegisterMutation,
  SaveCashRegisterMutationVariables,
  UpdateCashRegisterDocument,
  UpdateCashRegisterMutation,
  UpdateCashRegisterMutationVariables,
} from 'src/app/graphql/types';

@Injectable({
  providedIn: 'root',
})
export class CashRegisterService {
  private readonly apollo = inject(Apollo);

  get(id: number): Observable<CashRegister | null> {
    return this.apollo
      .watchQuery<{ getCashRegisterById: CashRegister }>({
        query: GetCashRegisterByIdDocument,
        variables: {
          id: id,
        },
      })
      .valueChanges.pipe(
        map(result => {
          return result.data ? result.data.getCashRegisterById : null;
        }),
      );
  }

  getAllByBusinessUnitId(
    businessUnitId: number,
  ): Observable<CashRegister[] | null> {
    return this.apollo
      .watchQuery<{ getCashRegisterByBusinessUnitId: CashRegister[] }>({
        query: GetCashRegisterByBusinessUnitIdDocument,
        variables: {
          businessUnitId: businessUnitId,
        },
      })
      .valueChanges.pipe(
        map(result => {
          return result.data
            ? result.data.getCashRegisterByBusinessUnitId
            : null;
        }),
      );
  }

  getByIdToPointEmission(id: number): Observable<CashRegister | null> {
    return this.apollo
      .watchQuery<{ getCashRegisterById: CashRegister }>({
        query: GetCashRegisterByIdToPointEmissionDocument,
        variables: {
          id: id,
        },
      })
      .valueChanges.pipe(
        map(result => {
          return result.data ? result.data.getCashRegisterById : null;
        }),
      );
  }

  getToPointEmission(businessUnitId: number): Observable<CashRegister | null> {
    return this.apollo
      .watchQuery<{ getCashRegisterByBusinessUnitId: CashRegister }>({
        query: GetCashRegisterToPointEmissionDocument,
        variables: {
          businessUnitId: businessUnitId,
        },
      })
      .valueChanges.pipe(
        map(result => {
          if (result.data) return result.data.getCashRegisterByBusinessUnitId;
          else return null;
        }),
      );
  }

  getCashRegisterNumber(businessUnitId: number): Observable<GenericNumber> {
    return this.apollo
      .watchQuery<{ getNextCashRegisterNumber: GenericNumber }>({
        query: GetNextCashRegisterNumberDocument,
        variables: {
          businessUnitId: businessUnitId,
        },
      })
      .valueChanges.pipe(
        map(result => {
          if (result.data) return result.data.getNextCashRegisterNumber;
          else return { number: -1 };
        }),
      );
  }

  getCashRegisterCatalogs(): Observable<GetCashRegisterCatalogsQuery | null> {
    return this.apollo
      .watchQuery<GetCashRegisterCatalogsQueryVariables>({
        query: GetCashRegisterCatalogsDocument,
      })
      .valueChanges.pipe(
        map(result => {
          return result.data ? result.data : null;
        }),
      );
  }

  save(form: FormGroup): Observable<IdDto | null> {
    return this.apollo
      .mutate<SaveCashRegisterMutation, SaveCashRegisterMutationVariables>({
        mutation: SaveCashRegisterDocument,
        variables: {
          json: this.jsonIn(form),
        },
      })
      .pipe(
        map(result => {
          return result.data ? (result.data.saveCashRegister as IdDto) : null;
        }),
      );
  }

  update(form: FormGroup): Observable<IdDto | null> {
    return this.apollo
      .mutate<UpdateCashRegisterMutation, UpdateCashRegisterMutationVariables>({
        mutation: UpdateCashRegisterDocument,
        variables: {
          json: this.jsonIn(form),
        },
      })
      .pipe(
        map(result => {
          return result.data ? (result.data.updateCashRegister as IdDto) : null;
        }),
      );
  }

  delete(id: number): Observable<MutationResult<DeleteCashRegisterMutation>> {
    return this.apollo.mutate<
      DeleteCashRegisterMutation,
      DeleteCashRegisterMutationVariables
    >({
      mutation: DeleteCashRegisterDocument,
      variables: {
        id: id.toString(),
      },
    });
  }

  private jsonIn(form: FormGroup): CashRegisterIn {
    return {
      id: form.get('id')?.value,
      businessUnitId: form.get('businessUnitId')?.value || null,
      name: form.get('name')?.value || null,
      activity: {
        id: form.get('economicActivity')?.value || null,
      },
      sriEnvironment: {
        id: form.get('sriEnvironment')?.value || null,
      },
      type: {
        id: form.get('type')?.value || null,
      },
      user: {
        id: form.get('cashRegisterUser')?.value || null,
      },
      active: form.get('active')?.value,
    };
  }
}
